import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { ResendRequestModal } from '@/components/treatment/v3/request/resend/ResendRequestModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { TreatmentRecordRequestSchema } from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import type { MedicalProvider } from '@/services/types/treatment-types';

const formatProvidedRecords = (providedRecords?: string[] | null) => {
  if (!providedRecords || providedRecords.length === 0) {
    return 'Records Request';
  }
  return providedRecords.join(', ');
};

const getRelatedRequestStats = (
  request: TreatmentRecordRequestSchema,
  requests: TreatmentRecordRequestSchema[],
) => {
  const currentMetadata = (request.metadata ?? {}) as Record<string, unknown>;
  const currentSupersededBy =
    typeof currentMetadata.superseded_by_request_id === 'string'
      ? currentMetadata.superseded_by_request_id
      : null;
  const currentForkedTo =
    typeof currentMetadata.forked_to_request_id === 'string'
      ? currentMetadata.forked_to_request_id
      : null;

  let previousAttempts = 0;

  const otherRequests = requests.filter((item) => item.id !== request.id);
  otherRequests.forEach((item) => {
    const metadata = (item.metadata ?? {}) as Record<string, unknown>;
    const itemSupersededBy =
      typeof metadata.superseded_by_request_id === 'string'
        ? metadata.superseded_by_request_id
        : null;
    const itemForkedTo =
      typeof metadata.forked_to_request_id === 'string'
        ? metadata.forked_to_request_id
        : null;

    if (
      itemSupersededBy === request.id ||
      itemForkedTo === request.id ||
      currentSupersededBy === item.id ||
      currentForkedTo === item.id
    ) {
      previousAttempts += 1;
    }
  });

  return {
    relatedRequests: otherRequests.length,
    previousAttempts,
  };
};

interface TreatmentRequestEntrySheetProps {
  matterId: string;
  provider: MedicalProvider;
  requestId: string;
  compactForMatter?: boolean;
}

export const TreatmentRequestEntrySheet = ({
  matterId,
  provider,
  requestId,
  compactForMatter = false,
}: TreatmentRequestEntrySheetProps) => {
  const [isResendModalOpen, setIsResendModalOpen] = React.useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

  const { data: request } =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsRetrieveQuery(
      {
        matterId,
        providerId: provider.id,
        requestId,
      },
      {
        skip: !matterId || !provider.id || !requestId,
      },
    );

  const { data: requests = [] } =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsListQuery(
      {
        matterId,
        providerId: provider.id,
      },
      {
        skip: !matterId || !provider.id,
      },
    );

  const [deleteRequest, { isLoading: isDeleting }] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsDestroyMutation();

  const handleDelete = React.useCallback(async () => {
    if (!request) return;
    try {
      await deleteRequest({
        matterId,
        providerId: provider.id,
        requestId: request.id,
      }).unwrap();
      toast.success('Request deleted');
      setIsDeleteDialogOpen(false);
    } catch {
      toast.error('Failed to delete request');
    }
  }, [deleteRequest, matterId, provider.id, request]);

  if (!request) {
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-400">
        <span className="h-3 w-24 animate-pulse rounded bg-slate-100" />
        <span className="text-slate-200">&middot;</span>
        <span className="h-3 w-24 animate-pulse rounded bg-slate-100" />
        <span className="text-slate-200">&middot;</span>
        <span className="h-3 w-16 animate-pulse rounded bg-slate-100" />
      </div>
    );
  }

  const { relatedRequests, previousAttempts } = getRelatedRequestStats(
    request,
    requests,
  );

  const lineItemClassName =
    'rounded-sm px-0.5 py-0 text-xs font-medium text-slate-500 transition-colors hover:text-slate-700 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500';

  return (
    <>
      <div className="mt-0.5 w-full border-b border-slate-200 pb-2.5">
        <div
          className={
            compactForMatter
              ? 'flex flex-wrap items-center gap-x-1.5 gap-y-1.5 text-[11px] leading-4'
              : 'flex items-center gap-1.5 overflow-x-auto whitespace-nowrap text-[11px] leading-4 no-scrollbar'
          }
        >
          <span className="shrink-0 font-medium text-slate-500">
            {relatedRequests} Related Request{relatedRequests === 1 ? '' : 's'}
          </span>
          <span className="shrink-0 text-slate-300">&middot;</span>
          <span
            className={
              previousAttempts > 0
                ? 'shrink-0 font-medium text-red-600'
                : 'shrink-0 font-medium text-slate-500'
            }
          >
            {previousAttempts} Previous Attempt
            {previousAttempts === 1 ? '' : 's'}
          </span>
          <span className="shrink-0 text-slate-300">&middot;</span>
          {compactForMatter ? <span className="basis-full h-0" /> : null}
          {!compactForMatter ? (
            <>
              <Link
                to={`/matters/${matterId}/treatment`}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-[11px] font-medium text-blue-600 underline-offset-2 hover:underline"
              >
                View in Matter
              </Link>
              <span className="shrink-0 text-slate-300">&middot;</span>
            </>
          ) : null}
          <button
            type="button"
            onClick={() => setIsResendModalOpen(true)}
            className={`${lineItemClassName} shrink-0 text-[11px]`}
          >
            Resend
          </button>
          <span className="shrink-0 text-slate-300">&middot;</span>
          <button
            type="button"
            onClick={() => setIsDeleteDialogOpen(true)}
            className={`${lineItemClassName} shrink-0 text-[11px]`}
          >
            Delete Request
          </button>
        </div>
      </div>

      <ResendRequestModal
        open={isResendModalOpen}
        onClose={() => setIsResendModalOpen(false)}
        matterId={matterId}
        providerId={provider.id}
        requestId={request.id}
        providerServiceId={provider.provider_service_ref_id}
        recordType={request.provided_records?.[0]}
        currentScope={formatProvidedRecords(request.provided_records)}
        currentChannelType={request.channel?.type}
        currentChannelValue={
          request.channel?.fax ||
          request.channel?.email ||
          request.channel?.address ||
          request.channel?.phone_number ||
          undefined
        }
      />

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Request</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the request from the provider workflow. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
